import type { InventoryHealthVariant } from "./inventory_health_cell_context";

const variants = (
  ...values: InventoryHealthVariant[]
): readonly InventoryHealthVariant[] => Object.freeze(values);

/**
 * Frontend Nucleus enablement must stay fail-closed. The nine active customers
 * and the existing GlowCo integration are listed explicitly so adding a
 * customer cannot accidentally expose a backend capability.
 */
const INVENTORY_HEALTH_VARIANTS_BY_CUSTOMER: Readonly<
  Record<string, readonly InventoryHealthVariant[]>
> = Object.freeze({
  goodr: variants("with_transfers"),
  oofos: variants("raw"),
  starface_world: variants("standard"),
  cora: variants("for_hubs"),
  cleanboss: variants("standard", "with_transfers"),
  goodchop: variants("standard"),
  brooklinen: variants("with_transfers"),
  vincero: variants("standard"),
  peach_and_lily: variants("standard"),
  glowco: variants("standard"),
});

/**
 * Dedicated contributor lineage is intentionally narrower than the existing
 * generic saved-value verifier. GlowCo keeps the latter until its producer and
 * reader contracts are explicitly onboarded.
 */
const INVENTORY_HEALTH_UNFULFILLED_DEMAND_VARIANTS_BY_CUSTOMER: Readonly<
  Record<string, readonly InventoryHealthVariant[]>
> = Object.freeze({
  goodr: variants("with_transfers"),
  oofos: variants("raw"),
  starface_world: variants("standard"),
  cora: variants("for_hubs"),
  cleanboss: variants("standard", "with_transfers"),
  goodchop: variants("standard"),
  brooklinen: variants("with_transfers"),
  vincero: variants("standard"),
  peach_and_lily: variants("standard"),
});

type InventoryHealthUnfulfilledDemandPeriodField = "week" | "month";
type InventoryHealthUnfulfilledDemandRecipe = Readonly<{
  variant: InventoryHealthVariant;
  periodField: InventoryHealthUnfulfilledDemandPeriodField;
  dimensionFields: readonly ("sku" | "location")[];
}>;

const recipe = (
  variant: InventoryHealthVariant,
  periodField: InventoryHealthUnfulfilledDemandPeriodField,
  dimensionFields: readonly ("sku" | "location")[],
): InventoryHealthUnfulfilledDemandRecipe =>
  Object.freeze({
    variant,
    periodField,
    dimensionFields: Object.freeze(dimensionFields),
  });

const SKU_LOCATION = Object.freeze(["sku", "location"] as const);
const LOCATION_SKU = Object.freeze(["location", "sku"] as const);
const LOCATION = Object.freeze(["location"] as const);

const INVENTORY_HEALTH_UNFULFILLED_DEMAND_RECIPES_BY_CUSTOMER: Readonly<
  Record<string, readonly InventoryHealthUnfulfilledDemandRecipe[]>
> = Object.freeze({
  goodr: Object.freeze([recipe("with_transfers", "week", SKU_LOCATION)]),
  oofos: Object.freeze([
    recipe("raw", "week", LOCATION_SKU),
    recipe("raw", "month", LOCATION),
  ]),
  starface_world: Object.freeze([recipe("standard", "week", SKU_LOCATION)]),
  cora: Object.freeze([recipe("for_hubs", "week", SKU_LOCATION)]),
  cleanboss: Object.freeze([
    recipe("standard", "week", SKU_LOCATION),
    recipe("with_transfers", "week", SKU_LOCATION),
  ]),
  goodchop: Object.freeze([recipe("standard", "week", SKU_LOCATION)]),
  brooklinen: Object.freeze([
    recipe("with_transfers", "week", SKU_LOCATION),
    recipe("with_transfers", "month", SKU_LOCATION),
  ]),
  vincero: Object.freeze([recipe("standard", "week", SKU_LOCATION)]),
  peach_and_lily: Object.freeze([recipe("standard", "week", SKU_LOCATION)]),
});

export const isCommonNucleusExplainabilityCustomer = (
  customerId: string,
): boolean =>
  Object.prototype.hasOwnProperty.call(
    INVENTORY_HEALTH_VARIANTS_BY_CUSTOMER,
    customerId,
  );

export const supportsCommonNucleusInventoryHealthVariant = (
  customerId: string,
  variant: InventoryHealthVariant,
): boolean =>
  INVENTORY_HEALTH_VARIANTS_BY_CUSTOMER[customerId]?.includes(variant) === true;

export const supportsCommonNucleusInventoryHealthUnfulfilledDemand = (
  customerId: string,
  variant: InventoryHealthVariant,
): boolean =>
  INVENTORY_HEALTH_UNFULFILLED_DEMAND_VARIANTS_BY_CUSTOMER[
    customerId
  ]?.includes(variant) === true;

export const commonNucleusInventoryHealthUnfulfilledDemandRecipe = (
  customerId: unknown,
  variant: InventoryHealthVariant,
  periodField: InventoryHealthUnfulfilledDemandPeriodField,
): readonly ("sku" | "location")[] | undefined =>
  typeof customerId === "string"
    ? INVENTORY_HEALTH_UNFULFILLED_DEMAND_RECIPES_BY_CUSTOMER[customerId]?.find(
        (candidate) =>
          candidate.variant === variant &&
          candidate.periodField === periodField,
      )?.dimensionFields
    : undefined;
